class VideoElement extends HTMLElement {
  /** @type {HTMLVideoElement | null} */
  #video = this.querySelector('video');
  /** @type {HTMLButtonElement | null} */
  #button = this.querySelector('button[data-play-pause-button]') ?? this.querySelector('button');
  /** @type {boolean} */
  #lazy = this.hasAttribute('data-lazy');
  /** @type {boolean} */
  #autoplay = this.hasAttribute('data-autoplay');
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;
  /** @type {boolean} */
  #userInteracted = false;

  connectedCallback() {
    if (!this.#video) return;

    this.#handleReducedMotion();

    if (this.#lazy) {
      this.#intersectionObserver = new IntersectionObserver(this.#handleIntersection.bind(this));
      this.#intersectionObserver.observe(this.#video);
    }

    if (this.#button) {
      this.#button.addEventListener('click', this.#handleClick.bind(this));
    }
  }

  disconnectedCallback() {
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    if (this.#button) {
      this.#button.removeEventListener('click', this.#handleClick.bind(this));
    }
  }

  /**
   * Try to play the video. If the video is not supported, show the play button.
   * @returns {void}
   */
  #tryPlay() {
    const promise = this.#video.play();
    if (promise !== undefined) {
      promise.catch(() => {
        this.#button?.classList.remove('hidden');
      });
    }
  }

  /**
   * Handle intersection of the video element.
   * @param {IntersectionObserverEntry[]} entries - The intersection observer entries.
   * @returns {void}
   */
  #handleIntersection(entries) {
    entries.forEach((entry) => {
      // Only play/pause video if user has interacted with it or if autoplay is enabled.
      const isToggleNeeded = this.#userInteracted || this.#autoplay;
      if (!isToggleNeeded) return;

      entry.isIntersecting ? this.#tryPlay() : this.#video.pause();
      this.#toggleButton();
    });
  }

  /**
   * Handle reduced motion preference by disabling autoplay and pausing video.
   * @returns {void}
   */
  #handleReducedMotion() {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches && this.#autoplay) {
      this.#autoplay = false;
      this.#video.pause();
      this.#toggleButton();
    }
  }

  /**
   * Handle button click to toggle video play/pause state.
   * @returns {void}
   */
  #handleClick() {
    this.#video.paused ? this.#video.play() : this.#video.pause();
    this.#userInteracted = true;
    this.#toggleButton();
  }

  /** Toggle visibility of play and pause icons based on video state. */
  #toggleButton() {
    /** @type {HTMLElement | null} */
    const playIcon = this.#button?.querySelector('[data-icon="regular-play"]');
    /** @type {HTMLElement | null} */
    const pauseIcon = this.#button?.querySelector('[data-icon="regular-pause"]');

    playIcon?.classList.toggle('hidden', !this.#video.paused);
    pauseIcon?.classList.toggle('hidden', this.#video.paused);
  }
}

if (!customElements.get('video-element')) {
  customElements.define('video-element', VideoElement);
}
